import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import {
  ArrowLeft, Plus, Save, Trash2, Loader2, Upload, X, Eye, EyeOff,
  BarChart3, MousePointer, FileText, TrendingUp, ChevronDown, ChevronUp,
  Megaphone, Image as ImageIcon
} from 'lucide-react';
import api from '../../utils/api';


const PLACEMENT_TYPES = [
  { value: 'homepage_carousel', label: 'Homepage Carousel' },
  { value: 'search_boost', label: 'Search Boost' },
  { value: 'treatment_page', label: 'Treatment Page' },
];

const PAYMENT_STATUSES = ['pending', 'paid', 'expired', 'cancelled'];

// ═══════════════════════════════════════════════════════════
// STATS OVERVIEW
// ═══════════════════════════════════════════════════════════

const StatsOverview = ({ stats }) => {
  const totals = stats.reduce((acc, s) => ({
    impressions: acc.impressions + s.impressions,
    clicks: acc.clicks + s.clicks,
    quote_requests: acc.quote_requests + s.quote_requests,
  }), { impressions: 0, clicks: 0, quote_requests: 0 });
  totals.ctr = totals.impressions > 0 ? (totals.clicks / totals.impressions * 100).toFixed(2) : '0.00';

  const cards = [
    { label: 'Total Impressions', value: totals.impressions.toLocaleString(), Icon: Eye, color: 'text-blue-600 bg-blue-50' },
    { label: 'Total Clicks', value: totals.clicks.toLocaleString(), Icon: MousePointer, color: 'text-emerald-600 bg-emerald-50' },
    { label: 'Overall CTR', value: `${totals.ctr}%`, Icon: TrendingUp, color: 'text-amber-600 bg-amber-50' },
    { label: 'Quotes Generated', value: totals.quote_requests.toLocaleString(), Icon: FileText, color: 'text-violet-600 bg-violet-50' },
  ];

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6" data-testid="stats-overview">
      {cards.map((c, i) => (
        <Card key={i} className="border border-slate-200">
          <CardContent className="p-4 flex items-center gap-3">
            <div className={`h-10 w-10 rounded-xl flex items-center justify-center ${c.color}`}>
              <c.Icon className="h-5 w-5" />
            </div>
            <div>
              <div className="text-lg font-bold text-[#122033]">{c.value}</div>
              <div className="text-[10px] text-slate-400 uppercase tracking-wider">{c.label}</div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════
// PLACEMENT FORM
// ═══════════════════════════════════════════════════════════

const PlacementForm = ({ placement, onSave, onCancel, onImageUpload, uploading }) => {
  const [form, setForm] = useState({
    clinic_id: '', campaign_id: '', placement_type: 'homepage_carousel',
    title: '', subtitle: '', image_url: '', treatment: '', price_from: 0,
    destination: '', start_date: '', end_date: '', active: true, display_order: 0,
    payment_status: 'pending', price: '', currency: 'EUR', invoice_id: '',
    target_countries: [], target_languages: [], target_treatments: [],
    priority_level: 'standard', max_exposure_percentage: '',
    ...placement,
  });

  const update = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  return (
    <Card className="border-2 border-[#3BA4C5]/30 mb-4" data-testid="placement-form">
      <CardContent className="p-5 space-y-4">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-base font-bold text-[#122033]">
            {placement?.placement_id ? 'Edit Placement' : 'New Sponsored Placement'}
          </h3>
          <button onClick={onCancel} className="h-8 w-8 rounded-lg hover:bg-slate-100 flex items-center justify-center">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-slate-500 mb-1 block">Title *</label>
            <Input value={form.title} onChange={e => update('title', e.target.value)}
              placeholder="e.g. Premium Hair Transplant" data-testid="form-title" />
          </div>
          <div>
            <label className="text-xs text-slate-500 mb-1 block">Treatment *</label>
            <Input value={form.treatment} onChange={e => update('treatment', e.target.value)}
              placeholder="e.g. Hair Transplant" data-testid="form-treatment" />
          </div>
          <div>
            <label className="text-xs text-slate-500 mb-1 block">Subtitle</label>
            <Input value={form.subtitle} onChange={e => update('subtitle', e.target.value)}
              placeholder="Short description" data-testid="form-subtitle" />
          </div>
          <div>
            <label className="text-xs text-slate-500 mb-1 block">Clinic ID *</label>
            <Input value={form.clinic_id} onChange={e => update('clinic_id', e.target.value)}
              placeholder="e.g. clinic-001" data-testid="form-clinic-id" />
          </div>
          <div>
            <label className="text-xs text-slate-500 mb-1 block">Price From (EUR)</label>
            <Input type="number" value={form.price_from} onChange={e => update('price_from', parseFloat(e.target.value) || 0)}
              data-testid="form-price" />
          </div>
          <div>
            <label className="text-xs text-slate-500 mb-1 block">Destination URL</label>
            <Input value={form.destination} onChange={e => update('destination', e.target.value)}
              placeholder="/clinics/clinic-001" data-testid="form-destination" />
          </div>
          <div>
            <label className="text-xs text-slate-500 mb-1 block">Placement Type</label>
            <select value={form.placement_type} onChange={e => update('placement_type', e.target.value)}
              className="w-full border border-slate-200 rounded-md p-2 text-sm" data-testid="form-type">
              {PLACEMENT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs text-slate-500 mb-1 block">Display Order</label>
            <Input type="number" value={form.display_order} onChange={e => update('display_order', parseInt(e.target.value) || 0)}
              data-testid="form-order" />
          </div>
          <div>
            <label className="text-xs text-slate-500 mb-1 block">Start Date</label>
            <Input type="datetime-local" value={form.start_date || ''} onChange={e => update('start_date', e.target.value || null)}
              data-testid="form-start-date" />
          </div>
          <div>
            <label className="text-xs text-slate-500 mb-1 block">End Date</label>
            <Input type="datetime-local" value={form.end_date || ''} onChange={e => update('end_date', e.target.value || null)}
              data-testid="form-end-date" />
          </div>
        </div>

        {/* Image Upload */}
        <div>
          <label className="text-xs text-slate-500 mb-1 block">Placement Image</label>
          <div className="flex items-center gap-3">
            {form.image_url && (
              <img src={form.image_url} alt="placement" className="h-16 w-24 object-cover rounded-lg border" />
            )}
            <label className="cursor-pointer inline-flex items-center gap-2 px-3 py-2 bg-slate-100 hover:bg-slate-200 rounded-lg text-sm transition-colors">
              <Upload className="h-4 w-4" />
              {uploading ? 'Uploading...' : 'Upload Image'}
              <input type="file" accept="image/*" className="hidden" onChange={e => {
                if (e.target.files?.[0]) onImageUpload(e.target.files[0], (url) => update('image_url', url));
              }} data-testid="form-image-upload" />
            </label>
            {form.image_url && (
              <Input value={form.image_url} onChange={e => update('image_url', e.target.value)}
                placeholder="Or paste image URL" className="flex-1 text-xs" />
            )}
          </div>
        </div>

        {/* Targeting Fields */}
        <details className="group" open>
          <summary className="text-xs text-slate-500 cursor-pointer hover:text-slate-700 flex items-center gap-1 font-medium">
            <ChevronDown className="h-3 w-3 group-open:rotate-180 transition-transform" />
            Targeting & Priority
          </summary>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2 p-3 bg-blue-50/50 rounded-lg border border-blue-100">
            <div>
              <label className="text-xs text-slate-500 mb-1 block">Target Countries (comma-separated codes)</label>
              <Input value={(form.target_countries || []).join(', ')}
                onChange={e => update('target_countries', e.target.value.split(',').map(s => s.trim()).filter(Boolean))}
                placeholder="e.g. DE, UK, NL" data-testid="form-target-countries" />
            </div>
            <div>
              <label className="text-xs text-slate-500 mb-1 block">Target Languages (comma-separated)</label>
              <Input value={(form.target_languages || []).join(', ')}
                onChange={e => update('target_languages', e.target.value.split(',').map(s => s.trim()).filter(Boolean))}
                placeholder="e.g. en, de, fr" data-testid="form-target-languages" />
            </div>
            <div>
              <label className="text-xs text-slate-500 mb-1 block">Target Treatments (comma-separated)</label>
              <Input value={(form.target_treatments || []).join(', ')}
                onChange={e => update('target_treatments', e.target.value.split(',').map(s => s.trim().toLowerCase().replace(/_/g, '-').replace(/\s+/g, '-')).filter(Boolean))}
                placeholder="e.g. hair-transplant, dental" data-testid="form-target-treatments" />
            </div>
            <div>
              <label className="text-xs text-slate-500 mb-1 block">Priority Level</label>
              <select value={form.priority_level || 'standard'} onChange={e => update('priority_level', e.target.value)}
                className="w-full border border-slate-200 rounded-md p-2 text-sm" data-testid="form-priority-level">
                <option value="standard">Standard (1.0x)</option>
                <option value="premium">Premium (1.5x)</option>
                <option value="featured">Featured (2.0x)</option>
              </select>
            </div>
          </div>
        </details>

        {/* Future Payment Fields */}
        <details className="group">
          <summary className="text-xs text-slate-400 cursor-pointer hover:text-slate-600 flex items-center gap-1">
            <ChevronDown className="h-3 w-3 group-open:rotate-180 transition-transform" />
            Payment Fields (Future Use)
          </summary>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-2 p-3 bg-slate-50 rounded-lg">
            <div>
              <label className="text-xs text-slate-500 mb-1 block">Payment Status</label>
              <select value={form.payment_status} onChange={e => update('payment_status', e.target.value)}
                className="w-full border border-slate-200 rounded-md p-2 text-sm" data-testid="form-payment-status">
                {PAYMENT_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-slate-500 mb-1 block">Price</label>
              <Input type="number" value={form.price || ''} onChange={e => update('price', parseFloat(e.target.value) || null)}
                placeholder="0.00" data-testid="form-payment-price" />
            </div>
            <div>
              <label className="text-xs text-slate-500 mb-1 block">Invoice ID</label>
              <Input value={form.invoice_id || ''} onChange={e => update('invoice_id', e.target.value)}
                placeholder="Optional" data-testid="form-invoice-id" />
            </div>
          </div>
        </details>

        <div className="flex items-center justify-between pt-2 border-t border-slate-100">
          <div className="flex items-center gap-2">
            <Switch checked={form.active} onCheckedChange={v => update('active', v)} data-testid="form-active-toggle" />
            <span className="text-sm text-slate-600">{form.active ? 'Active' : 'Inactive'}</span>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onCancel} data-testid="form-cancel-btn">Cancel</Button>
            <Button onClick={() => onSave(form)} className="bg-[#3BA4C5] hover:bg-[#2d8aab]" data-testid="form-save-btn">
              <Save className="h-4 w-4 mr-1" /> {placement?.placement_id ? 'Update' : 'Create'}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// ═══════════════════════════════════════════════════════════
// PLACEMENT CARD
// ═══════════════════════════════════════════════════════════

const PlacementCard = ({ placement, stats, onEdit, onDelete, onToggle }) => {
  const pStats = stats.find(s => s.placement_id === placement.placement_id) || {};
  const isExpired = placement.end_date && new Date(placement.end_date) < new Date();

  return (
    <Card className={`border transition-all ${placement.active && !isExpired ? 'border-slate-200' : 'border-slate-200 opacity-60'}`}
      data-testid={`placement-card-${placement.placement_id}`}>
      <CardContent className="p-4">
        <div className="flex items-start gap-4">
          {/* Image */}
          <div className="flex-none w-20 h-14 rounded-lg bg-slate-100 overflow-hidden">
            {placement.image_url ? (
              <img src={placement.image_url} alt="" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <ImageIcon className="h-6 w-6 text-slate-300" />
              </div>
            )}
          </div>

          {/* Details */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="text-sm font-semibold text-[#122033] truncate">{placement.title || placement.treatment || 'Untitled'}</h3>
              <Badge className="bg-[#3BA4C5]/10 text-[#3BA4C5] text-[9px] border-0">Sponsored</Badge>
              <Badge variant="outline" className="text-[9px]">{PLACEMENT_TYPES.find(t => t.value === placement.placement_type)?.label || placement.placement_type}</Badge>
              {isExpired && <Badge variant="destructive" className="text-[9px]">Expired</Badge>}
              {!placement.active && <Badge variant="secondary" className="text-[9px]"><EyeOff className="h-3 w-3 mr-0.5" />Hidden</Badge>}
            </div>
            <p className="text-xs text-slate-400 mb-1.5">
              {placement.treatment && <span>{placement.treatment} &middot; </span>}
              Clinic: {placement.clinic_id || 'N/A'}
              {placement.price_from > 0 && <span> &middot; From &euro;{placement.price_from.toLocaleString()}</span>}
            </p>
            {/* Mini stats */}
            <div className="flex gap-4 text-[10px] text-slate-400">
              <span>{pStats.impressions || 0} impressions</span>
              <span>{pStats.clicks || 0} clicks</span>
              <span>CTR: {pStats.ctr || 0}%</span>
              <span>{pStats.quote_requests || 0} quotes</span>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2 flex-none">
            <Switch checked={placement.active} onCheckedChange={(v) => onToggle(placement.placement_id, v)}
              data-testid={`toggle-${placement.placement_id}`} />
            <Button variant="ghost" size="sm" onClick={() => onEdit(placement)} data-testid={`edit-${placement.placement_id}`}>
              Edit
            </Button>
            <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700" onClick={() => onDelete(placement.placement_id)}
              data-testid={`delete-${placement.placement_id}`}>
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// ═══════════════════════════════════════════════════════════
// MAIN PAGE
// ═══════════════════════════════════════════════════════════

export default function AdminSponsoredPlacements() {
  const navigate = useNavigate();
  const [placements, setPlacements] = useState([]);
  const [stats, setStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [filter, setFilter] = useState('all');

  const getToken = () => '';
  const headers = () => ({ 'Content-Type': 'application/json' });

  const fetchData = useCallback(async () => {
    try {
      const [placementsRes, statsRes] = await Promise.all([
        api.get(`/admin/sponsored-placements`),
        api.get(`/admin/sponsored-placements/stats`),
      ]);
      if (placementsRes.ok) {
        const d = placementsRes.data;
        setPlacements(d.placements || []);
      }
      if (statsRes.ok) {
        const d = statsRes.data;
        setStats(d.stats || []);
      }
    } catch (err) {
      console.error('[AdminSponsored]', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSave = async (form) => {
    try {
      const isEdit = !!form.placement_id;
      const url = isEdit
        ? `/admin/sponsored-placements/${form.placement_id}`
        : `/admin/sponsored-placements`;
      const method = isEdit ? 'PUT' : 'POST';

      const res = await fetch(url, { method, headers: headers(), body: JSON.stringify(form) });
      {
        setShowForm(false);
        setEditTarget(null);
        fetchData();
      }
    } catch (err) {
      console.error('[AdminSponsored] Save error:', err);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this sponsored placement?')) return;
    try {
      await api.delete(`/admin/sponsored-placements/${id}`);
      fetchData();
    } catch (err) {
      console.error('[AdminSponsored] Delete error:', err);
    }
  };

  const handleToggle = async (id, active) => {
    try {
      await api.put(`/admin/sponsored-placements/${id}`, { active });
      fetchData();
    } catch (err) {
      console.error('[AdminSponsored] Toggle error:', err);
    }
  };

  const handleImageUpload = async (file, onComplete) => {
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post(`/admin/sponsored-placements/upload-image`, {
        method: 'POST',
        body: formData,
      });
      {
        const data = res.data;
        onComplete(data.image_url);
      }
    } catch (err) {
      console.error('[AdminSponsored] Upload error:', err);
    } finally {
      setUploading(false);
    }
  };

  const filtered = filter === 'all' ? placements : placements.filter(p => p.placement_type === filter);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center" data-testid="admin-sponsored-loading">
        <Loader2 className="h-8 w-8 animate-spin text-[#3BA4C5]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-sponsored-placements">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/treatx-admin/dashboard')} data-testid="back-btn">
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-[#122033] flex items-center gap-2">
                <Megaphone className="h-6 w-6 text-[#3BA4C5]" /> Sponsored Placements
              </h1>
              <p className="text-sm text-slate-500">Manage sponsored content across landing page and search</p>
            </div>
          </div>
          <Button onClick={() => { setEditTarget(null); setShowForm(true); }}
            className="bg-[#3BA4C5] hover:bg-[#2d8aab] gap-2" data-testid="create-placement-btn">
            <Plus className="h-4 w-4" /> New Placement
          </Button>
        </div>

        {/* Performance Stats */}
        <StatsOverview stats={stats} />

        {/* Form */}
        {showForm && (
          <PlacementForm
            placement={editTarget}
            onSave={handleSave}
            onCancel={() => { setShowForm(false); setEditTarget(null); }}
            onImageUpload={handleImageUpload}
            uploading={uploading}
          />
        )}

        {/* Filter */}
        <div className="flex items-center gap-2 mb-4">
          {[{ v: 'all', l: 'All' }, ...PLACEMENT_TYPES.map(t => ({ v: t.value, l: t.label }))].map(f => (
            <button key={f.v} onClick={() => setFilter(f.v)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${filter === f.v ? 'bg-[#122033] text-white' : 'bg-white text-slate-500 border border-slate-200 hover:border-slate-300'}`}
              data-testid={`filter-${f.v}`}>
              {f.l}
            </button>
          ))}
          <span className="text-xs text-slate-400 ml-auto">{filtered.length} placement{filtered.length !== 1 ? 's' : ''}</span>
        </div>

        {/* Placement List */}
        <div className="space-y-3">
          {filtered.map(p => (
            <PlacementCard key={p.placement_id} placement={p} stats={stats}
              onEdit={(p) => { setEditTarget(p); setShowForm(true); }}
              onDelete={handleDelete} onToggle={handleToggle} />
          ))}
        </div>

        {filtered.length === 0 && !showForm && (
          <div className="text-center py-16" data-testid="no-placements">
            <Megaphone className="h-12 w-12 text-slate-200 mx-auto mb-3" />
            <p className="text-lg font-medium text-slate-400">No sponsored placements yet</p>
            <p className="text-sm text-slate-300 mb-4">Create your first placement to start monetizing</p>
            <Button onClick={() => { setEditTarget(null); setShowForm(true); }} className="bg-[#3BA4C5] hover:bg-[#2d8aab]">
              <Plus className="h-4 w-4 mr-1" /> Create Placement
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
